// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Box, Button, Group, Stack, Stepper, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Bundle, Questionnaire, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { Document, Loading } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconArrowRight, IconClock, IconListDetails, IconUsersGroup } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ConsentRelationship } from '../components/InformedConsentModal';
import { getConfig } from '../config';
import { getIntakeMedplumClient } from '../lib/bot-client';
import { botIdentifier } from '../lib/bot-identifier';
import { searchInsurancePayers } from '../lib/insurance-payers';
import { storeIntakeConsentSignature } from '../lib/intake-consent-signature';
import { SelfReferralForm } from './onboarding/SelfReferralForm';

const SELF_PAY_ONLY_ITEM_IDS = [
  'self-pay',
  'insurance-provider',
  'insurance-provider-id',
  'insurance-plan-name',
  'member-id',
  'group-number',
  'policyholder',
  'secondary-plan-name',
  'secondary-member-id',
] as const;

const SELF_PAY_INSURANCE_ITEM_IDS = SELF_PAY_ONLY_ITEM_IDS.filter((linkId) => linkId !== 'self-pay');

/**
 * When self-pay-only mode is active, strip the self-pay toggle and all insurance
 * fields from the questionnaire so they are never rendered to the patient.
 * Returns the original questionnaire unchanged when mode is off (same reference).
 */
export function applyModeToQuestionnaire(questionnaire: Questionnaire, selfPayOnly: boolean): Questionnaire {
  if (!selfPayOnly) {
    return questionnaire;
  }
  return {
    ...questionnaire,
    item: questionnaire.item?.filter((i) => !(SELF_PAY_ONLY_ITEM_IDS as readonly string[]).includes(i.linkId ?? '')),
  };
}

/**
 * Returns a copy of the QR item list with a `self-pay: true` item present exactly once.
 * Does not mutate the input.
 */
export function injectSelfPayItem(items: QuestionnaireResponseItem[]): QuestionnaireResponseItem[] {
  const selfPayItem: QuestionnaireResponseItem = { linkId: 'self-pay', answer: [{ valueBoolean: true }] };
  return [selfPayItem, ...items.filter((i) => i.linkId !== 'self-pay')];
}

/**
 * Removes insurance answers from a self-pay submission while preserving the
 * self-pay marker needed by the intake bot to accept the patient.
 */
export function sanitizeIntakeResponseItems(
  items: QuestionnaireResponseItem[],
  selfPay: boolean
): QuestionnaireResponseItem[] {
  if (!selfPay) {
    return items;
  }
  return items.filter((item) => !(SELF_PAY_INSURANCE_ITEM_IDS as readonly string[]).includes(item.linkId ?? ''));
}

const ONBOARDING_STEPS = [
  { label: 'About You', description: 'Name & Contact Info' },
  { label: 'Schedule Intake', description: 'Meet Your Team' },
  { label: 'Next Steps', description: 'Get Your Care Plan' },
];

type PageState = 'form' | 'ineligible' | 'email-blocked';

export function OnboardingPage(): JSX.Element {
  const selfPayOnly = import.meta.env.VITE_PATIENT_APP_SELF_PAY_ONLY === 'true';
  const supportEmail = 'support@polarissleep.com';
  const isSmallScreen = useMediaQuery('(max-width: 876px)');
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pageState, setPageState] = useState<PageState>('form');
  const [showIntro, setShowIntro] = useState(true);

  useEffect(() => {
    // Fetch the questionnaire from the public JSON file
    fetch('/public-questionnaires.json')
      .then((res) => res.json())
      .then((bundle: Bundle) => {
        const entry = bundle.entry?.find(
          (e) => e.resource?.resourceType === 'Questionnaire' && e.resource.name === 'self-referral-intake-form'
        );
        if (entry?.resource) {
          setQuestionnaire(entry.resource as Questionnaire);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleOnSubmit = useCallback(
    async (
      response: QuestionnaireResponse,
      signature?: string,
      agreementPdfBase64?: string,
      consentRelationship?: ConsentRelationship
    ) => {
      if (!questionnaire || submitting) {
        return;
      }

      setSubmitting(true);
      try {
        const baseItems = selfPayOnly ? injectSelfPayItem(response.item ?? []) : (response.item ?? []);
        const selfPay = baseItems.some(
          (i) => i.linkId === 'self-pay' && i.answer?.some((a) => a.valueBoolean === true)
        );
        const sanitizedItems = sanitizeIntakeResponseItems(baseItems, selfPay);

        const questionnaireResponse: QuestionnaireResponse = {
          ...response,
          item: sanitizedItems,
          resourceType: 'QuestionnaireResponse',
          questionnaire: `Questionnaire/${questionnaire.id ?? questionnaire.name}`,
          status: 'completed',
          authored: new Date().toISOString(),
        };

        const result = (await Sentry.startSpan(
          {
            op: 'bot.execute',
            name: 'Execute process-intake-form',
          },
          async () => {
            const client = await getIntakeMedplumClient();
            return client.executeBot(
              botIdentifier('process-intake-form'),
              { questionnaireResponse, organizationId: getConfig().organizationId },
              'application/json'
            );
          }
        )) as { eligible: boolean; uniqueId?: string; reason?: string };

        if (!result.eligible) {
          setPageState(result.reason === 'email-already-in-use' ? 'email-blocked' : 'ineligible');
          return;
        }

        if (signature && result.uniqueId) {
          storeIntakeConsentSignature(result.uniqueId, signature, agreementPdfBase64, consentRelationship);
        }

        // Redirect to onboarding page with unique ID
        window.location.href = `/onboarding/${result.uniqueId}`;
      } catch (error) {
        Sentry.captureException(error);
        showNotification({
          color: 'red',
          message: normalizeErrorString(error),
          autoClose: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [questionnaire, submitting]
  );

  if (loading) {
    return <Loading />;
  }

  if (!questionnaire) {
    return (
      <Document width={800}>
        <Alert color="red" title="Questionnaire not found">
          The intake form could not be found. Please contact support.
        </Alert>
      </Document>
    );
  }

  if (showIntro) {
    return (
      <>
        <img
          src="/Logo-Black.png"
          alt="PolarisSleep"
          style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
        />
        <Document width={800} bg={'var(--mantine-color-white)'}>
          <Title order={2} mb="md" ta="center">
            Patient self-intake
          </Title>
        </Document>
        <Document width={800} bg={'var(--mantine-color-white)'} px="xl">
          <Title order={3} ta="center" mb={4}>
            What to expect
          </Title>
          <Text ta="center" c="dimmed" mb="xl">
            A quick overview before you begin.
          </Text>
          <Stack gap="md">
            {(
              [
                {
                  icon: <IconClock size={22} style={{ flexShrink: 0, marginTop: 2 }} />,
                  title: 'How long will this take?',
                  body: "About five minutes. You'll fill in basic demographic information and schedule your intake appointment.",
                },
                {
                  icon: <IconListDetails size={22} style={{ flexShrink: 0, marginTop: 2 }} />,
                  title: 'What do I need to get started?',
                  body: "Your contact information, and your insurance card if you're using insurance.",
                },
                {
                  icon: <IconUsersGroup size={22} style={{ flexShrink: 0, marginTop: 2 }} />,
                  title: 'What happens after I book?',
                  body: 'You can fill out your medical history now or later — but complete it before your first appointment to save time with your clinician.',
                },
              ] as const
            ).map(({ icon, title, body }) => (
              <Box
                key={title}
                bg="gray.0"
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  padding: '18px 20px',
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                }}
              >
                <Box style={{ color: 'var(--mantine-color-polaris-5)', flexShrink: 0 }}>{icon}</Box>
                <Box>
                  <Text fw={700} mb={4}>
                    {title}
                  </Text>
                  <Text>{body}</Text>
                </Box>
              </Box>
            ))}
          </Stack>
        </Document>
        <Document width={800} bg={'var(--mantine-color-white)'}>
          <Group justify="space-between" align="center">
            <Text c="dimmed" size="sm">
              Polaris Sleep · Patient self-intake
            </Text>
            <Button rightSection={<IconArrowRight size={18} />} onClick={() => setShowIntro(false)}>
              Get started
            </Button>
          </Group>
        </Document>
      </>
    );
  }

  if (pageState === 'ineligible') {
    return (
      <>
        <img
          src="/Logo-Black.png"
          alt="PolarisSleep"
          style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
        />
        <Document width={800} bg={'var(--mantine-color-white)'}>
          <Title order={2} mb="md" ta="center">
            New Patient Intake Form
          </Title>
        </Document>
        <Document width={800} bg={'var(--mantine-color-white)'} px="xl">
          <Alert color="blue" title="Unfortunately, we are not able to serve you at this time">
            <Text mb="md">We currently cannot accept this insurance plan.</Text>
            <Text mb="md">
              If you believe this is an error or would like to learn more about our services, please contact our support
              team at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
            </Text>
            <Button variant="outline" onClick={() => setPageState('form')}>
              Go Back
            </Button>
          </Alert>
        </Document>
      </>
    );
  }

  if (pageState === 'email-blocked') {
    return (
      <>
        <img
          src="/Logo-Black.png"
          alt="PolarisSleep"
          style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
        />
        <Document width={800} bg={'var(--mantine-color-white)'}>
          <Title order={2} mb="md" ta="center">
            New Patient Intake Form
          </Title>
        </Document>
        <Document width={800} bg={'var(--mantine-color-white)'} px="xl">
          <Alert color="blue" title="We are unable to start an intake with this email">
            <Text mb="md">
              This email address cannot be used to start a new intake. If you believe this is an error, please contact
              our support team at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
            </Text>
            <Button variant="outline" onClick={() => setPageState('form')}>
              Go Back
            </Button>
          </Alert>
        </Document>
      </>
    );
  }

  return (
    <>
      <img
        src="/Logo-Black.png"
        alt="PolarisSleep"
        style={{ display: 'block', margin: '24px auto 16px', width: 171 }}
      />
      <Document width={800} bg={'var(--mantine-color-white)'}>
        <Title order={2} mb="md" ta="center">
          New Patient Intake Form
        </Title>

        <Box
          style={{
            display: isSmallScreen ? 'flex' : 'block',
            justifyContent: 'center',
            border: '1px solid var(--mantine-color-gray-0)',
            borderRadius: 'var(--mantine-radius-md)',
          }}
          bg="gray.0"
        >
          <Stepper active={0} orientation={isSmallScreen ? 'vertical' : 'horizontal'} px={'xl'} py={'lg'}>
            {ONBOARDING_STEPS.map((step) => (
              <Stepper.Step key={step.label} label={step.label} description={step.description} />
            ))}
          </Stepper>
        </Box>
      </Document>
      <Document width={800} bg={'var(--mantine-color-white)'} px="xl">
        <Title order={3} ta="center" mb={4}>
          About you
        </Title>
        <Text ta="center" c="dimmed" mb="xl">
          To start, please tell us a bit about yourself.
        </Text>

        <Box px="xl">
          <SelfReferralForm
            questionnaire={applyModeToQuestionnaire(questionnaire, selfPayOnly)}
            onInsuranceSearch={searchInsurancePayers}
            onSubmit={handleOnSubmit}
            submitting={submitting}
          />
        </Box>
      </Document>
    </>
  );
}
